import { useEffect, useState } from 'react';
import { ISSUE_KIND_LABEL, type PhotoInfo } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { fmtDateTime } from '../lib/format';
import { Dialog } from './ui';

const KIND = { pickup: '積む', drop: '届ける', alcohol: '検知器', issue: ISSUE_KIND_LABEL.other } as const;

/** 写真を並べる。開くたびにサーバーで復号し、画面を閉じたら手放す（保存しない） */
export function PhotoGrid({ query }: { query: string }) {
  const [items, setItems] = useState<(PhotoInfo & { url?: string })[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const urls: string[] = [];
    let alive = true;
    api.get<{ photos: PhotoInfo[] }>(`/v1/office/photos?${query}`).then(async ({ photos }) => {
      if (alive) setItems(photos);
      for (const p of photos) {
        const blob = await api.blob(`/v1/office/photos/${p.id}`).catch(() => null);
        if (!blob || !alive) continue;
        const url = URL.createObjectURL(blob);
        urls.push(url);
        setItems((x) => x?.map((y) => (y.id === p.id ? { ...y, url } : y)) ?? null);
      }
    }).catch((e) => setErr(e instanceof HttpError ? e.message : '読み込めませんでした'));
    return () => { alive = false; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [query]);
  if (err) return <p className="err" role="alert">{err}</p>;
  if (!items) return <p className="muted">読み込み中…</p>;
  if (!items.length) return <p className="muted">写真はありません</p>;
  const one = (p: (typeof items)[number]) => (
    <figure key={p.id} style={{ margin: 0 }}>
      {p.url ? <a href={p.url} target="_blank" rel="noreferrer" title="押すと別のタブで大きく見られます"><img src={p.url} alt={`${KIND[p.kind]}の写真`} style={{ width: '100%', borderRadius: 8 }} /></a> : <div className="muted">開いています…</div>}
      <figcaption className="muted" style={{ fontSize: 12 }}>{KIND[p.kind]}・{fmtTime(p.takenAt)}</figcaption>
    </figure>
  );
  const grid = (list: typeof items) => <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>{list.map(one)}</div>;
  // 積んだときと届けたときの写真を左右に並べて比べる（荷物の傷・数の違いを見つけやすく。仕様書「ドライバー詳細・写真の比較」）
  const picks = items.filter((p) => p.kind === 'pickup'), drops = items.filter((p) => p.kind === 'drop');
  if (picks.length && drops.length && picks.length + drops.length === items.length) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }} aria-label="写真の比較">
        <section aria-label="積んだとき"><b style={{ display: 'block', marginBottom: 6 }}>積んだとき（{picks.length} 枚）</b>{grid(picks)}</section>
        <section aria-label="届けたとき"><b style={{ display: 'block', marginBottom: 6 }}>届けたとき（{drops.length} 枚）</b>{grid(drops)}</section>
      </div>
    );
  }
  return grid(items);
}

const fmtTime = (iso: string) => fmtDateTime(iso);

export function PhotoDialog({ title, query, onClose }: { title: string; query: string; onClose: () => void }) {
  return (
    <Dialog title={title} onClose={onClose} wide>
      <PhotoGrid query={query} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn" onClick={onClose}>閉じる</button></div>
    </Dialog>
  );
}
